import apiClient from './apiClient';

// Tipuri pentru API
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  field?: string;
  exists?: boolean;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: User;
  error?: string;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
}

export interface VeterinarianData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  cui: string;
  phone: string;
  specialization: string;
  experience: number;
  clinicId?: number;
  role?: string;
  status?: string;
}

export interface ClinicData {
  cui: string;
  clinicName: string;
  headquartersAddress: {
    street: string;
    streetNumber: string;
    city: string;
    county: string;
    postalCode: string;
    country: string;
    sector?: string;
  };
  workAddress: {
    street: string;
    streetNumber: string;
    city: string;
    county: string;
    postalCode: string;
    country: string;
    sector?: string;
  };
  phone: string;
  email: string;
  website: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  password: string;
  confirmPassword: string;
  schedule: {
    monday: { start: string; end: string; isOpen: boolean };
    tuesday: { start: string; end: string; isOpen: boolean };
    wednesday: { start: string; end: string; isOpen: boolean };
    thursday: { start: string; end: string; isOpen: boolean };
    friday: { start: string; end: string; isOpen: boolean };
    saturday: { start: string; end: string; isOpen: boolean };
    sunday: { start: string; end: string; isOpen: boolean };
  };
}

// Exportăm api ca alias pentru apiClient pentru compatibilitate
// Serviciile care folosesc api din authService vor funcționa fără modificări
export const api = apiClient;

export const authService = {
  // Login
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { token, user } = response.data;
      
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la autentificare',
      };
    }
  },

  // Logout
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  // Register
  async register(data: RegisterData): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/register', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la inregistrare',
      };
    }
  },

  // Register Veterinarian
  async registerVeterinarian(data: VeterinarianData): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/register-veterinarian', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la inregistrarea veterinarului',
      };
    }
  },

  // Register Clinic
  async registerClinic(data: ClinicData): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/register-clinic', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la inregistrarea clinicii',
      };
    }
  },

  // Verify Email
  async verifyEmail(token: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/verify-email', { token });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la verificarea email-ului',
      };
    }
  },

  // Resend Verification Email
  async resendVerificationEmail(email: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la retrimiterea email-ului de verificare',
      };
    }
  },

  // Forgot Password
  async forgotPassword(email: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la resetarea parolei',
      };
    }
  },

  // Reset Password
  async resetPassword(token: string, password: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/reset-password', { token, password });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la resetarea parolei',
      };
    }
  },

  // Check Tenant CUI
  async checkTenantCui(cui: string): Promise<ApiResponse> {
    // Folosim noul endpoint pentru verificarea CUI-ului
    try {
      const response = await apiClient.get(`/tenants/check-cui?cui=${cui}`);
      // Daca endpoint-ul returneaza eroare, returneaza eroarea
      if (!response.data.success) {
        return response.data;
      }

      return response.data;
    } catch (clinicError: any) {
      return { success: false, error: 'Eroare de retea/server. Incearca din nou.' };
    }
  },

  // Check Clinic CUI
  async checkClinicCui(cui: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.get(`/auth/verify-clinic-cui/${cui}`);
      // Daca endpoint-ul returneaza eroare, inseamna ca CUI-ul exista
      if (!response.data.success && response.data.code === 'CUI_ALREADY_EXISTS') {
        return { success: true, exists: true, message: response.data.error };
      }

      // Daca nu exista eroare, inseamna ca CUI-ul nu exista
      return { success: true, exists: false, message: 'CUI nu exista in baza de date locala' };
    } catch (clinicError: any) {
      // Daca eroarea este 409, inseamna ca CUI-ul exista
      if (clinicError.response?.status === 409 && clinicError.response?.data?.code === 'CUI_ALREADY_EXISTS') {
        return { success: true, exists: true, message: clinicError.response.data.error };
      }
      // Daca eroarea este 404, CUI-ul nu exista si putem continua
      if (clinicError.response?.status === 404 && (
        clinicError.response?.data?.code === 'CUI_NOT_FOUND' ||
        clinicError.response?.data?.code === 'CUI_NOT_FOUND_CAN_CONTINUE'
      )) {
        return { success: true, exists: false, message: clinicError.response.data.error || 'CUI nu exista in baza de date locala' };
      }
      
      return { success: false, error: 'Eroare de retea/server. Incearca din nou.' };
    }
  },

  // Verify Veterinarian CUI
  async verifyVeterinarianCui(cui: string): Promise<ApiResponse<any>> {
    // Folosim noul endpoint pentru verificarea CUI-ului
    try {
      const response = await apiClient.get(`/tenants/check-cui?cui=${cui}`);
      return response.data;
    } catch (tenantError: any) {
      return { success: false, error: 'Eroare de retea/server. Incearca din nou.' };
    }
  },

  // Activate Staff Account
  async activateStaffAccount(data: {
    token: string;
    firstName: string;
    lastName: string;
    password: string;
  }): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/staff/activate', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la activarea contului de staff',
      };
    }
  },

  // Activate Account
  async activateAccount(token: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/activate', { token });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la activarea contului',
      };
    }
  },

  // Get Current User
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get('/auth/me');
      return response.data;
    } catch (error) {
      return null;
    }
  },

  // Update Profile
  async updateProfile(data: Partial<User>): Promise<ApiResponse> {
    try {
      const response = await apiClient.put('/auth/profile', data);
      return response.data;
    } catch (error: any) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Eroare la actualizarea profilului',
      };
    }
  },

  // Change Password
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ authService.changePassword - Eroare:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la schimbarea parolei',
      };
    }
  },

  // Delete Account
  async deleteAccount(): Promise<ApiResponse> {
    try {
      const response = await apiClient.delete('/auth/account');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Eroare la stergerea contului',
      };
    }
  },
};

export default authService;
